import type { PracticeKind } from './domain'
import type { Locale } from './i18n/locales'

/*
 * A course's content as the course file (`course.yaml`), the editor's server and the editor's
 * pages know it (ADR 0009): the course document's shape, how long each text and each list of an
 * element may be, the rules across elements, and how a refused field is named. The validators
 * that apply them are packages/core's (content/format.ts and content/document.ts); the pages
 * read the limits here (`@eklevo/shared/course`), so that both are written once.
 */

/** Limits of each element of a course [T]: texts in characters, durations in minutes. */
export const CONTENT_LIMITS = {
  /** A human id of the course file, and a series. */
  slug: 60,
  course: {
    title: 200,
    /** The short one, which the cards and Noa read. */
    description: 500,
    /** What the course page says of the course besides the short description. */
    longDescription: 3000,
    objectives: 2000,
    minutes: 10_000,
    lessons: 200,
    levels: 5,
  },
  lesson: {
    title: 200,
    description: 3000,
    objectives: 2000,
    /** Only Noa reads them (ADR 0010 §4). */
    keyIdeas: 2000,
    minutes: 600,
    practice: 3,
    reflection: 4,
    materials: 10,
    questions: 20,
  },
  practice: { title: 200, instructions: 3000, minutes: 600, noaScenario: 2000 },
  reflection: { prompt: 500, intro: 1000 },
  material: { name: 200, description: 300 },
  question: { prompt: 1000, options: 4, option: 500, explanation: 2000 },
  assessment: {
    title: 200,
    minutes: 600,
    passThreshold: 100,
    defaultPassThreshold: 70,
    maxAttempts: 20,
    defaultMaxAttempts: 3,
    questions: 60,
  },
} as const

/** The longest link to a video or a material. */
export const LINK_MAX = 2000

/** A slug: lowercase letters and digits in words joined by hyphens. */
export const SLUG = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

/** Why an intermediate assessment cannot go where it is. */
export type PlacementProblem = 'unknown-lesson' | 'lesson-taken' | 'after-last-lesson'

/**
 * What is wrong with where each intermediate assessment goes (ADR 0006): right after a lesson of
 * the course, at most one after each lesson, and none after the last lesson when there is a
 * final assessment, which already follows it. `afterLessons` are the lessons the assessments
 * name, in their order; each problem says which of them it is about.
 */
export function placementProblems(
  lessons: readonly string[],
  afterLessons: readonly string[],
  hasFinal: boolean,
): { index: number; problem: PlacementProblem }[] {
  const problems: { index: number; problem: PlacementProblem }[] = []
  const placed = new Set<string>()
  afterLessons.forEach((lesson, index) => {
    if (!lessons.includes(lesson)) problems.push({ index, problem: 'unknown-lesson' })
    else if (placed.has(lesson)) problems.push({ index, problem: 'lesson-taken' })
    else placed.add(lesson)
    if (lesson === lessons.at(-1) && hasFinal)
      problems.push({ index, problem: 'after-last-lesson' })
  })
  return problems
}

/**
 * Which of a series and a level is missing when the other is there, if one is: they go together,
 * because a level is the place of a course in its series (ADR 0006).
 */
export function seriesLevelProblem(
  series: string | null | undefined,
  level: number | null | undefined,
): 'series' | 'level' | null {
  const hasSeries = series !== null && series !== undefined
  const hasLevel = level !== null && level !== undefined
  if (hasSeries === hasLevel) return null
  return hasSeries ? 'level' : 'series'
}

/*
 * The course document: a version of a course in memory, as the editor saves it while it is
 * written (ADR 0009 §2) and as the importer reads it from `course.yaml`. Every element that is
 * edited or moved on its own has a stable key (a UUID): lessons, assessments and questions keep
 * the keys of their rows, which progress, attempts and reflections key on; reflection moments
 * and materials have one for the editor to follow them as they move. A file is referenced by the
 * id of its row in `files`. packages/core/src/content/document.ts checks it, as a draft or to
 * publish.
 */

export interface QuestionDocument {
  key: string
  prompt: string
  /** Four of them to publish; a draft may have fewer, or empty ones. */
  options: string[]
  /** The correct option, 0-based; null while it is not chosen yet, only in a draft. */
  correct: number | null
  explanation: string
}

/** A practice activity, as a lesson stores it (PracticeActivity). */
export interface PracticeDocument {
  kind: PracticeKind
  title: string
  instructions: string
  durationMinutes?: number
  /** Only for kind 'noa': the scenario given to Noa as context. */
  noaScenario?: string
}

export interface ReflectionMomentDocument {
  key: string
  prompt: string
}

/**
 * A file's id is null while the file is still being uploaded, only in a draft. A video file's
 * captions are null when it has none, and their id is null while they are being uploaded.
 */
export type VideoDocument =
  | { kind: 'file'; fileId: string | null; captions: { fileId: string | null } | null }
  | { kind: 'external'; url: string }

/** A material's description says what the person finds in it; empty when there is none. */
export type MaterialDocument =
  | { key: string; kind: 'pdf'; name: string; description: string; fileId: string | null }
  | { key: string; kind: 'link'; name: string; description: string; url: string }

export interface LessonDocument {
  key: string
  title: string
  description: string
  objectives: string
  /**
   * What Noa keeps in mind about the lesson (ADR 0010 §4): optional, and only Noa reads it, never
   * the person. Empty when there are none.
   */
  keyIdeas: string
  durationMinutes: number | null
  video: VideoDocument | null
  practice: PracticeDocument[]
  reflection: ReflectionMomentDocument[]
  /** What the reflection exercise says before its moments; empty when it says nothing. */
  reflectionIntro: string
  materials: MaterialDocument[]
  test: QuestionDocument[]
}

export interface AssessmentDocument {
  key: string
  title: string
  estimatedMinutes: number | null
  passThreshold: number
  maxAttempts: number
  questions: QuestionDocument[]
}

/** A gate right after the lesson whose key it names. */
export interface IntermediateAssessmentDocument extends AssessmentDocument {
  afterLessonKey: string
}

export interface CourseDocument {
  title: string
  /** The short description, which the cards and Noa read. */
  description: string
  /** What the course page says besides it, and the course's objectives; empty when there are none. */
  longDescription: string
  objectives: string
  /** Courses of one owner with the same series are its levels (ADR 0006): a slug. */
  series: string | null
  /** Place of the course in its series; only with a series. */
  level: number | null
  language: Locale
  estimatedMinutes: number | null
  /** In order: each person moves through them at their own pace. */
  lessons: LessonDocument[]
  intermediateAssessments: IntermediateAssessmentDocument[]
  finalAssessment: AssessmentDocument | null
}

export type ValidationLevel = 'draft' | 'publish'

/** Why a field is refused, as the editor shows it there. */
export const CONTENT_PROBLEMS = [
  'required',
  'too-long',
  'too-few',
  'too-many',
  'out-of-range',
  'invalid',
  'invalid-characters',
  'not-https',
  'video-host',
  'only-for-noa',
  'duplicate-key',
  'unknown-lesson',
  'lesson-taken',
  'after-last-lesson',
  'series-and-level',
  'video-or-material-required',
  'file-not-clean',
  'file-wrong-kind',
  'file-not-found',
  'reflection-fixed',
  'level-taken',
] as const

export type ContentProblem = (typeof CONTENT_PROBLEMS)[number]

export interface DocumentIssue {
  /** Where the problem is, from the document (or the element) down to the field. */
  path: (string | number)[]
  code: ContentProblem
}
